import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../../utils/apiClient';

/**
 * Determines if text should be light or dark based on background color
 */
const getContrastTextColor = (hexColor) => {
  if (!hexColor) return '#6c757d';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#333333' : '#ffffff';
};

const PrioritiesModal = ({ isOpen, onClose }) => {
  const { instance, accounts } = useMsal();
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newPriorityName, setNewPriorityName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPriorities();
    }
  }, [isOpen]);

  const fetchPriorities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/priorities'
      );

      if (response.data.success) {
        setPriorities(response.data.data);
      } else {
        setError('Failed to load priorities');
      }
    } catch (err) {
      console.error('Error fetching priorities:', err);
      setError('Failed to load priorities');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPriority = async () => {
    if (!newPriorityName.trim()) {
      setError('Priority name cannot be empty');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const maxOrder = priorities.length > 0 
        ? Math.max(...priorities.map(p => p.display_order)) 
        : 0;

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/priorities',
        {
          priority_name: newPriorityName.trim(),
          display_order: maxOrder + 1
        }
      );

      if (response.data.success) {
        setNewPriorityName('');
        await fetchPriorities();
      } else {
        setError(response.data.message || 'Failed to add priority');
      }
    } catch (err) {
      console.error('Error adding priority:', err);
      setError(err.response?.data?.message || 'Failed to add priority');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (priority) => {
    setEditingId(priority.id);
    setEditingName(priority.priority_name);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setError(null);
  };

  const handleSaveEdit = async (priorityId) => {
    if (!editingName.trim()) {
      setError('Priority name cannot be empty');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/priorities/${priorityId}`,
        {
          priority_name: editingName.trim()
        }
      );

      if (response.data.success) {
        setEditingId(null);
        setEditingName('');
        await fetchPriorities();
      } else {
        setError(response.data.message || 'Failed to update priority');
      }
    } catch (err) {
      console.error('Error updating priority:', err);
      setError(err.response?.data?.message || 'Failed to update priority');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePriority = async (priorityId) => {
    if (!window.confirm('Are you sure you want to delete this priority? This will remove the priority from all matrix items that use it.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/priorities/${priorityId}`
      );

      if (response.data.success) {
        await fetchPriorities();
        if (response.data.data.affectedMatrixRows > 0) {
          alert(`Priority deleted. ${response.data.data.affectedMatrixRows} matrix items were affected.`);
        }
      } else {
        setError(response.data.message || 'Failed to delete priority');
      }
    } catch (err) {
      console.error('Error deleting priority:', err);
      setError(err.response?.data?.message || 'Failed to delete priority');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>Manage Priorities</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '30px',
              height: '30px'
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Add New Priority */}
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#333' }}>
            Add New Priority
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={newPriorityName}
              onChange={(e) => setNewPriorityName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !saving) {
                  handleAddPriority();
                }
              }}
              placeholder="Enter priority name..."
              disabled={saving}
              style={{
                flex: 1,
                padding: '0.5rem',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            />
            <button
              onClick={handleAddPriority}
              disabled={saving || !newPriorityName.trim()}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: saving || !newPriorityName.trim() ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: saving || !newPriorityName.trim() ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem'
              }}
            >
              {saving ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>

        {/* Priorities List */}
        <div>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#333' }}>
            Existing Priorities ({priorities.length})
          </h3>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Loading priorities...
            </div>
          ) : priorities.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No priorities found. Add your first priority above.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {priorities.map((priority) => (
                <div
                  key={priority.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    backgroundColor: editingId === priority.id ? '#e7f3ff' : 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px'
                  }}
                >
                  {editingId === priority.id ? (
                    <>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !saving) {
                            handleSaveEdit(priority.id);
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                        disabled={saving}
                        autoFocus
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #007bff',
                          borderRadius: '4px',
                          fontSize: '0.9rem'
                        }}
                      />
                      <button
                        onClick={() => handleSaveEdit(priority.id)}
                        disabled={saving || !editingName.trim()}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: saving || !editingName.trim() ? 'not-allowed' : 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={saving}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Color swatch */}
                      <div
                        style={{
                          width: '60px',
                          height: '28px',
                          borderRadius: '4px',
                          backgroundColor: priority.color_code || '#ccc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: getContrastTextColor(priority.color_code),
                          border: '1px solid rgba(0,0,0,0.1)'
                        }}
                        title={priority.color_code || 'No color set'}
                      >
                        {priority.priority_name}
                      </div>
                      <span style={{ flex: 1, fontSize: '0.9rem', fontWeight: '500', marginLeft: '0.5rem' }}>
                        {priority.priority_name}
                        {priority.color_code && (
                          <span style={{ color: '#888', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                            ({priority.color_code})
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => handleStartEdit(priority)}
                        disabled={saving}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeletePriority(priority.id)}
                        disabled={saving}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{
          marginTop: '1.5rem',
          paddingTop: '1rem',
          borderTop: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrioritiesModal;

